/**
 * SmartEmptyState — Contextual Empty States (V107)
 *
 * No module should ever appear blank.
 * Every empty state has: explanation + primary CTA + optional secondary.
 *
 * Props:
 *   title          — main message (e.g. "No data yet")
 *   description    — contextual explanation
 *   primaryCTA     — {label, onClick, path?}
 *   secondaryCTA   — {label, onClick} optional
 *   icon           — Lucide icon component
 *   hint           — optional small hint text
 *   compact        — for inline use (no full-screen centering)
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { BarChart2, RefreshCw } from "lucide-react";

export default function SmartEmptyState({
  title       = "No data yet",
  description = "Get started to see data here.",
  primaryCTA,
  secondaryCTA,
  icon:     Icon = BarChart2,
  hint,
  compact   = false,
}) {
  const navigate = useNavigate();

  const handlePrimary = () => {
    if (primaryCTA?.onClick) primaryCTA.onClick();
    else if (primaryCTA?.path) navigate(primaryCTA.path);
  };

  const handleSecondary = () => {
    if (secondaryCTA?.onClick) secondaryCTA.onClick();
    else if (secondaryCTA?.path) navigate(secondaryCTA.path);
  };

  if (compact) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-5 text-center"
        data-testid="smart-empty-state">
        <Icon size={18} className="text-slate-300 mx-auto mb-2" />
        <p className="text-xs font-semibold text-slate-600 mb-0.5">{title}</p>
        <p className="text-[9px] text-slate-400 mb-3">{description}</p>
        {primaryCTA && (
          <button onClick={handlePrimary}
            className="px-3 py-1.5 bg-teal-600 text-white text-[9px] font-bold rounded-xl hover:bg-teal-700">
            {primaryCTA.label}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center"
      data-testid="smart-empty-state">
      <div className="w-16 h-16 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center mb-4">
        <Icon size={22} className="text-slate-300" />
      </div>
      <p className="text-sm font-bold text-slate-700 mb-1.5">{title}</p>
      <p className="text-[10px] text-slate-400 max-w-xs leading-relaxed mb-5">{description}</p>

      <div className="flex flex-col items-center gap-2">
        {primaryCTA && (
          <button onClick={handlePrimary}
            className="px-5 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 transition-colors"
            data-testid="empty-state-primary-cta">
            {primaryCTA.label}
          </button>
        )}
        {secondaryCTA && (
          <button onClick={handleSecondary}
            className="px-4 py-2 text-xs text-slate-400 hover:text-slate-600 font-medium">
            {secondaryCTA.label}
          </button>
        )}
      </div>
      {hint && (
        <p className="text-[8px] text-slate-300 mt-4 italic max-w-xs">{hint}</p>
      )}
    </div>
  );
}

// ── Pre-built empty state presets ──────────────────────────────────────────────
import { DollarSign, Shield, Map, BarChart3, FileText, Target } from "lucide-react";

export const FINoDealsEmptyState = ({ navigate }) => (
  <SmartEmptyState
    icon={DollarSign}
    title="No deals in pipeline yet"
    description="Deals appear here when SMEs, projects, or corporates submit financing requests. Once submitted, they move through Screening → Due Diligence → Credit Committee → Close."
    primaryCTA={{ label: "Open FI Operating System →", path: "/fi/workspace" }}
    secondaryCTA={{ label: "Learn how deals flow into FI", onClick: () => {} }}
    hint="Source: SME workspace Financing Readiness → FI intake | Project workspace → FI | State workspace → Programme submission"
    data-testid="fi-no-deals"
  />
);

export const SMENoPeriodsEmptyState = () => (
  <SmartEmptyState
    icon={BarChart2}
    title="No monthly data yet"
    description="Enter your first month of activity data — electricity, fuel, and transport usage — to start tracking your emissions. Your first calculation takes less than 2 minutes."
    primaryCTA={{ label: "Enter First Month →", path: "/sme/monthly/inputs" }}
    hint="After your first calculation, you'll see emissions by scope, trend analysis, and financing readiness."
    data-testid="sme-no-periods"
  />
);

export const CredentialsNotAvailableEmptyState = ({ workspace, navigate }) => (
  <SmartEmptyState
    icon={Shield}
    title="Credentials not yet available"
    description="Calculate and lock at least one reporting period to generate your Climate & Circularity Credentials. Credentials include a 7-section institutional document with full calculation traceability."
    primaryCTA={{ label: "Enter Monthly Data →", path: `/${workspace}/monthly/inputs` }}
    hint="Tip: Higher confidence scores (≥75) enable Institutional PDF generation and VVB verification."
  />
);

export const StatesNoProgrammesEmptyState = ({ stateId }) => (
  <SmartEmptyState
    icon={Map}
    title="No programmes for this state"
    description="Create a PoA or CPA programme to aggregate eligible projects into a state-level financing vehicle. Programmes with projects can be submitted directly to the FI pipeline."
    primaryCTA={{ label: "Create First Programme →", path: stateId ? `/states/${stateId}/programmes` : "/states" }}
    hint="Programmes require at least 1 project with a valid sub-national state_id to pass cross-state validation."
  />
);

export const PortfolioIntelligenceEmptyState = () => (
  <SmartEmptyState
    icon={BarChart3}
    title="Portfolio Intelligence requires 3+ periods"
    description="Calculate at least 3 months of data to enable trajectory analysis, consistency scoring, and the Board Investment Narrative. More periods = more accurate projections."
    primaryCTA={{ label: "Calculate More Periods →", path: "/sme/monthly/inputs" }}
    compact
    hint="Trajectory analysis requires minimum 3 non-zero periods."
  />
);
